import React, { useState } from 'react';
import { 
  X, 
  Search, 
  Book, 
  Video, 
  ExternalLink, 
  ChevronLeft, 
  ChevronRight,
  Filter,
  Clock,
  Tag,
  User,
  BookOpen,
  Lightbulb,
  AlertCircle,
  FileText
} from 'lucide-react';
import { Card } from '@plvplus/core';
import { useHelpContext, HelpContent } from './HelpContextProvider';

interface HelpDocViewerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpDocViewer: React.FC<HelpDocViewerProps> = ({ isOpen, onClose }) => {
  const { 
    getContextualHelp, 
    getHelpByType, 
    searchHelp, 
    searchQuery,
    activeHelpId,
    currentContext
  } = useHelpContext();
  
  const [selectedArticle, setSelectedArticle] = useState<HelpContent | null>(null);
  const [selectedType, setSelectedType] = useState<HelpContent['type'] | 'all'>('all');
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  const contextualHelp = getContextualHelp();
  const filteredHelp = selectedType === 'all' 
    ? contextualHelp 
    : getHelpByType(selectedType);

  const handleSearch = (query: string) => {
    setLocalSearchQuery(query);
    searchHelp(query);
  };

  const getTypeIcon = (type: HelpContent['type']) => {
    switch (type) {
      case 'guide': return BookOpen;
      case 'tutorial': return Video;
      case 'reference': return FileText;
      case 'troubleshooting': return AlertCircle;
      default: return Book;
    }
  };

  const getTypeColor = (type: HelpContent['type']) => {
    switch (type) {
      case 'guide': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300';
      case 'tutorial': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300';
      case 'reference': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      case 'troubleshooting': return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const renderMarkdown = (content: string) => {
    // Simple markdown renderer - in production, use a proper markdown library
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return <h1 key={index} className="text-2xl font-bold text-gray-900 dark:text-white mb-4 mt-6">{line.substring(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-5">{line.substring(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">{line.substring(4)}</h3>;
        }
        if (line.startsWith('#### ')) {
          return <h4 key={index} className="text-base font-medium text-gray-900 dark:text-white mb-2 mt-3">{line.substring(5)}</h4>;
        }
        if (line.startsWith('- ')) {
          return <li key={index} className="text-gray-700 dark:text-gray-300 ml-4">{line.substring(2)}</li>;
        }
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={index} className="font-semibold text-gray-900 dark:text-white mb-2">{line.slice(2, -2)}</p>;
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return <p key={index} className="text-gray-700 dark:text-gray-300 mb-2 leading-relaxed">{line}</p>;
      });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex h-full">
          {/* Sidebar - Article List */}
          <div className="w-1/3 border-r border-gray-200 dark:border-gray-700 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Book className="w-5 h-5 mr-2" />
                  Documentation
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher dans l'aide..."
                  value={localSearchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>

              {/* Type Filter */}
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              >
                <option value="all">Tous les types</option>
                <option value="guide">Guides</option>
                <option value="tutorial">Tutoriels</option>
                <option value="reference">Référence</option>
                <option value="troubleshooting">Dépannage</option>
              </select>
            </div>

            {/* Context Info */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                Contexte actuel
              </h3>
              <div className="space-y-1 text-xs text-blue-700 dark:text-blue-400">
                <div>Page: {currentContext.page}</div>
                <div>Module: {currentContext.module}</div>
                {currentContext.section && <div>Section: {currentContext.section}</div>}
                <div>Rôle: {currentContext.userRole.join(', ')}</div>
              </div>
            </div>

            {/* Articles List */}
            <div className="flex-1 overflow-y-auto">
              {filteredHelp.length === 0 ? (
                <div className="p-4 text-center">
                  <Book className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Aucune aide trouvée pour ce contexte
                  </p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {filteredHelp.map((article) => {
                    const TypeIcon = getTypeIcon(article.type);
                    const isSelected = selectedArticle?.id === article.id;
                    
                    return (
                      <button
                        key={article.id}
                        onClick={() => setSelectedArticle(article)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <TypeIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1 truncate">
                              {article.title}
                            </h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                              {article.description}
                            </p>
                            <div className="flex items-center mt-2 space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(article.type)}`}>
                                {article.type}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(article.lastUpdated)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {selectedArticle ? (
              <>
                {/* Article Header */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {selectedArticle.title}
                      </h1>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {selectedArticle.description}
                      </p>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Mis à jour le {formatDate(selectedArticle.lastUpdated)}
                        </div>
                        <div className="flex items-center">
                          <Tag className="w-4 h-4 mr-1" />
                          {selectedArticle.tags.slice(0, 3).join(', ')}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      {selectedArticle.videoUrl && (
                        <a
                          href={selectedArticle.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-2 text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                        >
                          <Video className="w-4 h-4 mr-2" />
                          Voir la vidéo
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Article Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    {renderMarkdown(selectedArticle.content)}
                  </div>

                  {/* Related Articles */}
                  {selectedArticle.relatedArticles && selectedArticle.relatedArticles.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        Articles liés
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedArticle.relatedArticles.map((relatedId) => {
                          const relatedArticle = contextualHelp.find(a => a.id === relatedId);
                          if (!relatedArticle) return null;
                          
                          return (
                            <button
                              key={relatedId}
                              onClick={() => setSelectedArticle(relatedArticle)}
                              className="text-left p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                {relatedArticle.title}
                              </h4>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {relatedArticle.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Welcome Screen */
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="text-center max-w-md">
                  <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Centre d'aide PLV+
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Sélectionnez un article dans la liste pour commencer à lire la documentation contextuelle.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
                      <div className="font-medium text-blue-900 dark:text-blue-300">
                        {getHelpByType('guide').length} Guides
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <Video className="w-6 h-6 text-green-600 dark:text-green-400 mx-auto mb-2" />
                      <div className="font-medium text-green-900 dark:text-green-300">
                        {getHelpByType('tutorial').length} Tutoriels
                      </div>
                    </div>
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
                      <div className="font-medium text-purple-900 dark:text-purple-300">
                        {getHelpByType('reference').length} Références
                      </div>
                    </div>
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 mx-auto mb-2" />
                      <div className="font-medium text-red-900 dark:text-red-300">
                        {getHelpByType('troubleshooting').length} Dépannage
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
